import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface LandingPageContent {
  heroBadge: string;
  heroHeadline: string;
  heroSubhead: string;
  ctaText: string;
  problemTitle: string;
  problemPoints: string[];
  solutionTitle: string;
  solutionText: string;
  solutionPoints: string[];
  featuresHeadline: string;
  featuresSubhead: string;
  features: Array<{ icon: string; title: string; description: string }>;
  howItWorksTitle: string;
  howItWorksSteps: Array<{ title: string; description: string }>;
  pricingHeadline: string;
  pricingPlanName: string;
  pricingAmount: string;
  pricingPeriod: string;
  pricingFeatures: string[];
  emailHeadline: string;
  emailSubhead: string;
  emailCta: string;
  metaDescription: string;
}

export interface GenerateLandingPageResponse {
  success: boolean;
  slug: string;
  url: string;
  content: LandingPageContent;
  html: string;
}

export interface DeployLandingPageResponse {
  success: boolean;
  url: string;
  slug: string;
}

interface UseLandingPageGeneratorResult {
  isGenerating: boolean;
  isDeploying: boolean;
  error: string | null;
  generatedPage: GenerateLandingPageResponse | null;
  deployedUrl: string | null;
  generateLandingPage: (
    projectId: string,
    templateStyle?: string
  ) => Promise<GenerateLandingPageResponse | null>;
  deployLandingPage: (projectId: string) => Promise<DeployLandingPageResponse | null>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-powered landing page generation and deployment
 */
export function useLandingPageGenerator(): UseLandingPageGeneratorResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPage, setGeneratedPage] = useState<GenerateLandingPageResponse | null>(null);
  const [deployedUrl, setDeployedUrl] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setGeneratedPage(null);
    setDeployedUrl(null);
  }, []);

  const generateLandingPage = useCallback(
    async (
      projectId: string,
      templateStyle: string = "modern"
    ): Promise<GenerateLandingPageResponse | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setIsGenerating(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          { projectId: string; templateStyle: string },
          GenerateLandingPageResponse
        >(functions, "generateLandingPage");

        const result = await generateFunction({
          projectId,
          templateStyle,
        });

        setGeneratedPage(result.data);
        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate landing page";
        setError(errorMessage);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const deployLandingPage = useCallback(
    async (projectId: string): Promise<DeployLandingPageResponse | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setIsDeploying(true);
      setError(null);

      try {
        const functions = getFunctions();
        const deployFunction = httpsCallable<
          { projectId: string },
          DeployLandingPageResponse
        >(functions, "deployLandingPage");

        const result = await deployFunction({ projectId });

        setDeployedUrl(result.data.url);
        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to deploy landing page";
        setError(errorMessage);
        return null;
      } finally {
        setIsDeploying(false);
      }
    },
    []
  );

  return {
    isGenerating,
    isDeploying,
    error,
    generatedPage,
    deployedUrl,
    generateLandingPage,
    deployLandingPage,
    clearError,
    reset,
  };
}
